import { userStorage } from './userStorage';
// Kalender-Hilfsfunktionen für ZielplanungsKalender
// Ausgelagert im Rahmen des Phase-1-Refactorings (März 2026)

// ─── Datum-Helpers ────────────────────────────────────────────────────────────

export const getLocalDateStr = (d = new Date()) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

export const getWeekDates = (date) => {
  const curr = new Date(date);
  const dayOfWeek = curr.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const monday = new Date(curr);
  monday.setDate(curr.getDate() + mondayOffset);

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    dates.push(day);
  }
  return dates;
};

export const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

export const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

// ─── LocalStorage Helpers ─────────────────────────────────────────────────────

export const loadData = (key, defaultValue = {}) => {
  try {
    const data = userStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const saveData = (key, data) => {
  try {
    userStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving data:', e);
  }
};

// ─── Habit-Reminder Helpers ───────────────────────────────────────────────────

export const DELETED_HABIT_REMINDERS_KEY = 'zielplanung_deleted_habit_reminders';

export const getDeletedHabitReminders = () => {
  try { return new Set(JSON.parse(userStorage.getItem(DELETED_HABIT_REMINDERS_KEY) || '[]')); }
  catch { return new Set(); }
};

export const addDeletedHabitReminder = (id) => {
  try {
    const deleted = JSON.parse(userStorage.getItem(DELETED_HABIT_REMINDERS_KEY) || '[]');
    if (!deleted.includes(id)) {
      deleted.push(id);
      userStorage.setItem(DELETED_HABIT_REMINDERS_KEY, JSON.stringify(deleted));
    }
  } catch {}
};

export const generateHabitReminderAppointments = (habits, allLogs = {}) => {
  const result = [];
  const deletedSet = getDeletedHabitReminders();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  for (const habit of habits) {
    const freq = habit.schedule?.frequencyType;
    if (freq === 'weekly') continue;

    // ── Schichten: Kalender-Blöcke je Schichttyp ─────────────────────────────
    if (freq === 'schichten') {
      const zeiten   = habit.schedule?.schichtZeiten;
      const rhythmus = habit.schedule?.schichtRhythmus;
      if (!zeiten || !rhythmus?.days?.length || !rhythmus.cycleLength) continue;

      const hasAnyBlock = Object.values(zeiten).some(z => z?.calendarBlock);
      if (!hasAnyBlock) continue;

      const habitStart = habit.startDate || todayStr;
      const maxMonths  = Math.max(...Object.values(zeiten)
        .filter(z => z?.calendarBlock).map(z => z.months || 3));
      const consolidationEnd = new Date(habitStart + 'T12:00:00');
      consolidationEnd.setMonth(consolidationEnd.getMonth() + maxMonths);
      const endOfYear      = new Date(today.getFullYear(), 11, 31);
      const generationEnd  = consolidationEnd > endOfYear ? consolidationEnd : endOfYear;
      const startOfYear    = new Date(today.getFullYear(), 0, 1);
      const habitStartDate = new Date(habitStart + 'T00:00:00');
      const cursorStart    = habitStartDate > startOfYear ? habitStartDate : startOfYear;
      const cursor         = new Date(cursorStart);
      const rhythmusStart  = new Date(rhythmus.startDate + 'T12:00:00').getTime();

      const SCHICHT_COLORS = { Früh: 'orange', Spät: 'blue', Nacht: 'purple', Frei: 'teal' };

      while (cursor <= generationEnd) {
        const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,'0')}-${String(cursor.getDate()).padStart(2,'0')}`;
        if (dateStr >= habitStart) {
          const cursorNoon = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 12, 0, 0).getTime();
          const offset     = Math.round((cursorNoon - rhythmusStart) / 86400000);

          if (offset >= 0) {
            const pos     = ((offset % rhythmus.cycleLength) + rhythmus.cycleLength) % rhythmus.cycleLength;
            const schicht = rhythmus.days[pos] ?? 'Frei';
            const z       = zeiten[schicht];

            if (z?.calendarBlock) {
              const isEnabled = !z.type || z.type === 'alle' ||
                (z.type === 'bestimmte' && (z.enabledCyclePositions || []).includes(pos));

              if (isEnabled) {
                // Übersprungene Tage auslassen
                const isSkipped = (habit.skippedDates || []).includes(dateStr);
                if (!isSkipped) {
                  const baseTime   = z.time || '08:00';
                  const time       = (habit.dailyTimeOverrides || {})[dateStr] || baseTime;
                  const duration   = z.duration || 30;
                  const [sH, sM]   = time.split(':').map(Number);
                  const totalEnd   = sH * 60 + sM + duration;
                  const endTime    = `${String(Math.floor(totalEnd / 60) % 24).padStart(2,'0')}:${String(totalEnd % 60).padStart(2,'0')}`;
                  const aptId      = `hr_${habit.id}_${dateStr}`;

                  if (!deletedSet.has(aptId)) {
                    const logs      = allLogs[habit.id] || [];
                    const completed = logs.some(l => l.date === dateStr);
                    result.push({
                      id: aptId,
                      title: habit.title,
                      date: dateStr,
                      startTime: time,
                      endTime,
                      note: `${schicht}schicht: ${habit.title}\nDauer: ${duration} Min.`,
                      color: SCHICHT_COLORS[schicht] || 'blue',
                      isHabitReminder: true,
                      habitId: habit.id,
                      priority: 10,
                      completed
                    });
                  }
                }
              }
            }
          }
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      continue; // schichten fertig – nicht weiter unten verarbeiten
    }

    // ── Standard-Frequenzen (daily / custom) ─────────────────────────────────
    const cr = habit.calendarReminder;
    if (!cr?.enabled) continue;

    const { time = '08:00', consolidationMonths = 3, durationMinutes = 15 } = cr;
    const [startH, startM] = time.split(':').map(Number);
    const totalEndMinutes = startH * 60 + startM + durationMinutes;
    const endTime = `${String(Math.floor(totalEndMinutes / 60) % 24).padStart(2,'0')}:${String(totalEndMinutes % 60).padStart(2,'0')}`;

    // Generate from start of current year (for year view history) through full consolidation period
    const habitStart = habit.startDate || todayStr;
    const consolidationEnd = new Date(habitStart + 'T12:00:00');
    consolidationEnd.setMonth(consolidationEnd.getMonth() + consolidationMonths);
    // Also extend to end of current year to ensure full year view coverage
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    const generationEnd = consolidationEnd > endOfYear ? consolidationEnd : endOfYear;

    // Start from the beginning of the current year (or habit start, whichever is later)
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const habitStartDate = new Date(habitStart + 'T00:00:00');
    const cursorStart = habitStartDate > startOfYear ? habitStartDate : startOfYear;
    const cursor = new Date(cursorStart);

    while (cursor <= generationEnd) {
      const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,'0')}-${String(cursor.getDate()).padStart(2,'0')}`;
      if (dateStr >= habitStart) {
        const dow = cursor.getDay();
        const isDue = freq === 'daily' || (freq === 'custom' && (habit.schedule?.daysOfWeek || []).includes(dow));
        if (isDue) {
          // Übersprungene Tage (aus Konfliktauflösung) auslassen
          const isSkipped = (habit.skippedDates || []).includes(dateStr);
          if (!isSkipped) {
            const aptId = `hr_${habit.id}_${dateStr}`;
            if (!deletedSet.has(aptId)) {
              const cue = habit.ifThen?.cueCustom || habit.ifThen?.cuePreset || '';
              const logs = allLogs[habit.id] || [];
              const completed = logs.some(l => l.date === dateStr);
              // Tagesspezifische Zeit-Überschreibung verwenden (aus Konfliktauflösung)
              const dayTime = (habit.dailyTimeOverrides || {})[dateStr] || time;
              const [dayStartH, dayStartM] = dayTime.split(':').map(Number);
              const dayTotalEnd = dayStartH * 60 + dayStartM + durationMinutes;
              const dayEndTime = `${String(Math.floor(dayTotalEnd / 60) % 24).padStart(2,'0')}:${String(dayTotalEnd % 60).padStart(2,'0')}`;
              result.push({
                id: aptId,
                title: habit.title,
                date: dateStr,
                startTime: dayTime,
                endTime: dayEndTime,
                note: `Routine: ${habit.title}\nDauer: ${durationMinutes} Min.${cue ? '\nTrigger: ' + cue : ''}`,
                color: 'blue',
                isHabitReminder: true,
                habitId: habit.id,
                priority: 10,
                completed
              });
            }
          }
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return result;
};
